import React, { useState, useEffect, useContext } from "react";
// reactstrap Table kept for the scheduler grid (its cells/colours are grid internals).
import { Table } from "reactstrap";
import Select from "react-select";
import BatchDetailsModal from "./BatchDetailsModal";
import "./offlineBatch.scss";
import { getStudentsOfflineNew, getWeeklySlotsOffline,getTeachersOffline } from "../../../Api/apiUtils";
import { ConfigContext } from '../../../Api/global/ApiContext';
import { toast } from "react-toastify";
import { Page, PageHeader, Card, Button, Field, Toolbar, Loader, EmptyState } from "../../../ui";


// API Response Types
type TeacherSlot = {
  id: number;
  teacherId: number;
  centerId: number;
  studentCount: number;
  teacherName: string;
  ageGroups: string[];
  levels: any[];
};

type WeeklySlotsResponse = {
  days: {
    [dayName: string]: {
      [timeSlot: string]: TeacherSlot[];
    };
  };
};

// Offline Centres Data Types
type Course = {
  id: string;
  tagId: string;
  demoCourseId: string;
  name: string;
  image?: string;
};

type OfflineCentre = {
  locationName: string;
  locationAddress: string;
  centreName: string;
  centerId: number;
  centreId: number;
  city: string;
  name: string;
  courses: Course[];
};

type OfflineCentresData = {
  offlineCentres: OfflineCentre[];
};

// Component Types
type Batch = {
  id: string;
  teacher: string;
  level: string | any[];
  ageGroup: string;
  students: string;
  subject?: string;
  room?: string;
  description?: string;
  teacherId?: number;
  centerId?: number;
  studentCount?: number;
};

type DayKey = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';

type ScheduleData = {
  [time: string]: {
    [K in DayKey]: Batch[];
  };
};


// Helper function to transform API data to component format
const transformApiDataToSchedule = (apiData: WeeklySlotsResponse): ScheduleData => {
  const scheduleData: ScheduleData = {};
  
  console.log("Transforming API data:", apiData);
  
  // Day name mapping - handles both uppercase (FRIDAY) and capitalized (Friday) formats
  const dayMapping: { [key: string]: DayKey } = {
    "Monday": "Mon",
    "MONDAY": "Mon",
    "Tuesday": "Tue",
    "TUESDAY": "Tue", 
    "Wednesday": "Wed",
    "WEDNESDAY": "Wed",
    "Thursday": "Thu",
    "THURSDAY": "Thu",
    "Friday": "Fri",
    "FRIDAY": "Fri",
    "Saturday": "Sat",
    "SATURDAY": "Sat",
    "Sunday": "Sun",
    "SUNDAY": "Sun"
  };

  // Process API data and create time slots dynamically


  Object.entries(apiData.days).forEach(([dayName, timeSlots]) => {
    const dayKey = dayMapping[dayName];
    if (!dayKey) {
      console.warn(`Unknown day name: ${dayName}`);
      return;
    }

    console.log(`Processing day: ${dayName} -> ${dayKey}`, timeSlots);

    Object.entries(timeSlots).forEach(([timeRange, slots]) => {
      // Ensure slots is always an array
      const slotsArray = Array.isArray(slots) ? slots : [];
      
      if (slotsArray.length === 0) {
        return; // Skip empty slots
      }
      
      // Convert time range to display format
      const displayTime = convertTimeRangeToDisplay(timeRange);
      console.log(`Processing time: ${timeRange} -> ${displayTime}, slots:`, slotsArray);
      
      // Initialize time slot if it doesn't exist
      if (!scheduleData[displayTime]) {
        scheduleData[displayTime] = {
          Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: []
        };
      }
      
      // Add slots to the appropriate day (remove the condition check, just assign directly)
      scheduleData[displayTime][dayKey] = slotsArray.map(slot => ({
        id: slot.id.toString(),
        teacher: slot.teacherName,
        level: slot.levels && slot.levels.length > 0 ? slot.levels : "-",
        ageGroup: slot.ageGroups && slot.ageGroups.length > 0 ? slot.ageGroups.join(", ") : "Not Specified",
        students: `${slot.studentCount}/5`, // Assuming max 5 students
        teacherId: slot.teacherId,
        centerId: slot.centerId,
        studentCount: slot.studentCount
      }));
      
      console.log(`Assigned ${slotsArray.length} slots to ${displayTime}[${dayKey}]`);
    });
  });
  
  console.log("Final schedule data:", scheduleData);

  return scheduleData;
};

// Helper function to convert time range to display format
const convertTimeRangeToDisplay = (timeRange: string): string => {
  const [startTime, endTime] = timeRange.split('-');
  const startHour = parseInt(startTime.split(':')[0]);
  const endHour = parseInt(endTime.split(':')[0]);
  
  const formatHour = (hour: number) => {
    if (hour === 0) return "12 AM";
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return "12 PM";
    return `${hour - 12} PM`;
  };

  return `${formatHour(startHour)}-${formatHour(endHour)}`;
};






// API Integration Function - Uses real API data only
const fetchWeeklySlots = async (courseId: string, centerId: string, teacherId?: string, studentId?: string, ageGroup?: string): Promise<WeeklySlotsResponse> => {
  try {
    const response = await getWeeklySlotsOffline(courseId, centerId, teacherId, studentId, ageGroup);
    console.log("Raw API response:", response);
    
    // Check if response is a fetch Response object or direct data
    if (response && typeof response === 'object' && 'ok' in response) {
      // It's a fetch Response object
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data: WeeklySlotsResponse = await response.json();
    return data;
    } else {
      // It's direct data - ensure it has the correct structure
      const data = response as any;
      // If response has 'days' property, return as is
      if (data && data.days) {
        return data as WeeklySlotsResponse;
      }
      // If response is the days object directly, wrap it
      if (data && typeof data === 'object' && !data.days) {
        return { days: data } as WeeklySlotsResponse;
      }
      return data as WeeklySlotsResponse;
    }
  } catch (error) {
    throw error;
  }
};

const BatchScheduler: React.FC = () => {
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string>(""); // Will be set dynamically
  const [selectedCentre, setSelectedCentre] = useState<string>(""); // Will be set dynamically
  const [selectedCourse, setSelectedCourse] = useState<string>(""); // Will be set dynamically
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedTeacher, setSelectedTeacher] = useState<string>("");
  const [selectedStudent, setSelectedStudent] = useState<string>("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>("");
  const config = useContext(ConfigContext);
  const [students, setStudents] = useState<any[]>([]);
  const [teachers, setTeachers] = useState<any[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<any[]>([]);
  const [filteredTeachers, setFilteredTeachers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [teacherLoading, setTeacherLoading] = useState<boolean>(false);
  const [noResults, setNoResults] = useState<boolean>(false);
  const [teacherNoResults, setTeacherNoResults] = useState<boolean>(false);
  const [scheduleData, setScheduleData] = useState<ScheduleData>({});
  const [scheduleLoading, setScheduleLoading] = useState<boolean>(false);
  const [offlineCentresData, setOfflineCentresData] = useState<OfflineCentresData>(config?.
    offlineCentres );

  useEffect(() => {
    if (config?.
      offlineCentres) {
      setOfflineCentresData({ offlineCentres: config.
        offlineCentres });
    }
  }, [config?.offlineCentres]);

  const getCityOptions = () => {
    if (!offlineCentresData?.offlineCentres || !Array.isArray(offlineCentresData.offlineCentres)) {
      return [];
    }

    // Group by city field to show each city only once
    const uniqueCities = Array.from(new Set(offlineCentresData.offlineCentres.map(centre => centre.city)));
    return uniqueCities.map(city => ({
      value: city,
      label: city
    }));
  };

  const getCentreOptions = () => {
    if (!selectedCity || !offlineCentresData?.offlineCentres || !Array.isArray(offlineCentresData.offlineCentres)) {
      return [];
    }
    // Show all centres for the selected city (filter by city field)
    const filteredCentres = offlineCentresData.offlineCentres.filter(centre => centre.city === selectedCity);
 
    return filteredCentres.map(centre => ({
      value: centre.centreId?.toString() || "",
      label: centre.name || "Unknown Location" // Use name field for center display
    }));
  };

  const getCourseOptions = () => {
    if (!config?.grmpcourses || !Array.isArray(config.grmpcourses)) {
      return [];
    }
    
    // Filter for offline courses (courses with "offline" in the name)
    // Use v2Id as the value to pass to the weekly slots API
    const offlineCourses = config.grmpcourses.filter(course => 
      course.name && course.name.toLowerCase().includes('offline')
    );
    
    return offlineCourses.map(course => ({
      value: course.v2Id || course.id || "",
      label: course.name || "Unknown Course"
    }));
  };

  // Initialize default selections when data is available
  useEffect(() => {
    if (offlineCentresData?.offlineCentres && Array.isArray(offlineCentresData.offlineCentres) && offlineCentresData.offlineCentres.length > 0 && !selectedCity) {
      const firstCity = offlineCentresData.offlineCentres[0]?.city;
      if (firstCity) {
        setSelectedCity(firstCity);
      }
    }
  }, [offlineCentresData, selectedCity]);

  // Set default centre when city changes
  useEffect(() => {
    if (selectedCity && !selectedCentre && offlineCentresData?.offlineCentres && Array.isArray(offlineCentresData.offlineCentres)) {
      const centresForCity = offlineCentresData.offlineCentres.filter(
        centre => centre.city === selectedCity
      );
 
      if (centresForCity.length > 0) {
        setSelectedCentre(centresForCity[0].centreId?.toString() || "");
      }
    }
  }, [selectedCity, selectedCentre, offlineCentresData]);

  // Set default course when component mounts
  useEffect(() => {
    if (!selectedCourse && config?.grmpcourses && Array.isArray(config.grmpcourses)) {
      // Filter for offline courses and select the first one
      const offlineCourses = config.grmpcourses.filter(course => 
        course.name && course.name.toLowerCase().includes('offline')
      );
      
      if (offlineCourses.length > 0) {
        setSelectedCourse(offlineCourses[0].v2Id || offlineCourses[0].id);
      }
    }
  }, [selectedCourse, config?.grmpcourses]);


  const handleBatchClick = (batch: Batch) => {
    setSelectedBatch(batch);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedBatch(null);
    fetchScheduleData();
  };

  // Dynamic filter options
  const cityOptions = getCityOptions();
  const centreOptions = getCentreOptions();
  const courseOptions = getCourseOptions();

  const levelOptions = [
    { value: "Beginner", label: "Beginner" },
    { value: "Intermediate", label: "Intermediate" },
    { value: "Advanced", label: "Advanced" },
    { value: "Prep", label: "Prep" }
  ];

  const ageGroupOptions = [
    { value: "all", label: "All Age Groups" },
    { value: "6-12", label: "6-12" },
    { value: "13-21", label: "13-21" },
    { value: "22-33", label: "22-33" },
    { value: "31-40", label: "31-40" },

    { value: "40+", label: "40+" }
  ];

  // Generate teacher options from filtered teachers
  const teacherOptions = [
    { value: "all", label: "All Teachers" },
    ...filteredTeachers.map(teacher => ({
      value: teacher.id || teacher.teacherId || teacher._id,
      label: teacher.teacherName || teacher.name || 'Unknown Teacher'
    }))
  ];

  // Generate student options from filtered students
  const studentOptions = [
    { value: "all", label: "All Students" },
    ...filteredStudents.map(student => ({
      value: student.id || student.studentId || student._id,
      label: student.name || student.studentName || 'Unknown Student'
    }))
  ];

  const days: DayKey[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];


  const fetchStudents = async (searchPayload: { offlineCenterId?:any; studentName?: string;} = {}) => {
     setLoading(true);
     try {
       const response = await getStudentsOfflineNew( selectedCourse, selectedCentre);
       const fetchedStudents = response.content || [];

       if (Array.isArray(fetchedStudents)) {
         setStudents(fetchedStudents);
         setFilteredStudents(fetchedStudents);
         setNoResults(fetchedStudents.length === 0);
       } else {
         setNoResults(true);
       }
     } catch (error) {
       setNoResults(true);
     } finally {
       setLoading(false);
     }
   };

   const fetchTeachers = async (teacherName: string = "") => {
     setTeacherLoading(true);
     try {
       const response = await getTeachersOffline(selectedCentre, selectedCourse);
       const fetchedTeachers = response.content || [];

       if (Array.isArray(fetchedTeachers)) {
         setTeachers(fetchedTeachers);
         setFilteredTeachers(fetchedTeachers);
         setTeacherNoResults(fetchedTeachers.length === 0);
       } else {
         setTeacherNoResults(true);
       }
     } catch (error) {
       setTeacherNoResults(true);
     } finally {
       setTeacherLoading(false);
     }
   };

   // Frontend search functions
   const filterStudents = (searchTerm: string) => {
     if (!searchTerm.trim()) {
       setFilteredStudents(students);
     } else {
       const filtered = students.filter(student => 
         (student.name || student.studentName || '').toLowerCase().includes(searchTerm.toLowerCase())
       );
       setFilteredStudents(filtered);
     }
   };

   const filterTeachers = (searchTerm: string) => {
     if (!searchTerm.trim()) {
       setFilteredTeachers(teachers);
     } else {
       const filtered = teachers.filter(teacher => 
         (teacher.teacherName || teacher.name || '').toLowerCase().includes(searchTerm.toLowerCase())
       );
       setFilteredTeachers(filtered);
     }
   };

   // Fetch students when component mounts and when center/course changes
   useEffect(() => {
     if (selectedCentre && selectedCourse) {
       fetchStudents({ offlineCenterId: selectedCentre });
     }
   }, [selectedCentre, selectedCourse]);

   // Fetch teachers when center and course are selected
   useEffect(() => {
     if (selectedCentre && selectedCourse) {
       fetchTeachers();
     }
   }, [selectedCentre, selectedCourse]);

   // Handle city change
   const handleCityChange = (option: any) => {
     const newCity = option?.value || "";
     setSelectedCity(newCity);
     // Reset dependent selections when city changes
     setSelectedCentre("");
     setSelectedCourse("");
     setSelectedTeacher("");
     setSelectedStudent("");
     setSelectedAgeGroup("");
     setStudents([]);
     setTeachers([]);
     setFilteredStudents([]);
     setFilteredTeachers([]);
     setNoResults(false);
     setTeacherNoResults(false);
   };

   // Handle center change
   const handleCentreChange = (option: any) => {
     const newCenterId = option?.value || "";
     setSelectedCentre(newCenterId);
     // Reset dependent selections when center changes
     setSelectedCourse("");
     setSelectedTeacher("");
     setSelectedStudent("");
     setSelectedAgeGroup("");
     // Clear students and teachers list immediately to show loading state
     setStudents([]);
     setTeachers([]);
     setFilteredStudents([]);
     setFilteredTeachers([]);
     setNoResults(false);
     setTeacherNoResults(false);
   };

   // Handle course change
   const handleCourseChange = (option: any) => {
     const newCourseId = option?.value || "";
     setSelectedCourse(newCourseId);
     // Reset dependent selections when course changes
     setSelectedTeacher("");
     setSelectedStudent("");
     setSelectedAgeGroup("");
     // Clear teachers and students when course changes
     setTeachers([]);
     setStudents([]);
     setFilteredTeachers([]);
     setFilteredStudents([]);
     setTeacherNoResults(false);
     setNoResults(false);
   };

   // Fetch schedule data
   const fetchScheduleData = async () => {
     if (!selectedCourse || !selectedCentre) {
       return;
     }
     
     setScheduleLoading(true);
     try {
       const apiData = await fetchWeeklySlots(selectedCourse, selectedCentre, selectedTeacher, selectedStudent, selectedAgeGroup);
       const transformedData = transformApiDataToSchedule(apiData);
       setScheduleData(transformedData);
       
       // Check if a specific student is selected but no data is returned
       if (selectedStudent && selectedStudent !== "all") {
         const hasData = Object.values(transformedData).some(timeSlot => 
           Object.values(timeSlot).some(dayData => dayData.length > 0)
         );
         
         if (!hasData) {
           const selectedStudentName = studentOptions.find(option => option.value === selectedStudent)?.label || "Selected Student";
           toast.error(`${selectedStudentName} is not added in any class`);
         }
       }
     } catch (error) {
       console.error("Error fetching schedule data:", error);
       setScheduleData({});
     } finally {
       setScheduleLoading(false);
     }
   };

   // Fetch schedule data when center or course changes
  //  useEffect(() => {
  //    if (selectedCentre && selectedCourse) {
  //      fetchScheduleData();
  //    }
  //  }, [selectedCentre, selectedCourse]);

  return (
    <Page className="offline-batch-container">
      <PageHeader
        eyebrow="Offline"
        title="Batch Management"
        subtitle="Weekly offline batches by centre and course. Pick a batch to see or change its students."
      />

      {/* Filter Dropdowns */}
      <Card className="offline-batch__filters">
        {/* <h5 className="filter-title"> Offline Batch Management</h5> */}
        <Toolbar>
          <Field label="City" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={cityOptions.find(option => option.value === selectedCity)}
                onChange={handleCityChange}
                options={cityOptions}
                placeholder="Select City"
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </Field>
          <Field label="Centre" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={centreOptions.find(option => option.value === selectedCentre)}
                onChange={handleCentreChange}
                options={centreOptions}
                placeholder={selectedCity ? "Select Centre" : "Select City first"}
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                isDisabled={!selectedCity}
              />
            )}
          </Field>
          <Field label="Course" required>
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={courseOptions.find(option => option.value === selectedCourse)}
                onChange={handleCourseChange}
                options={courseOptions}
                placeholder="Select Course"
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </Field>
          {/* <Field label="Level">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                value={levelOptions.find(option => option.value === selectedLevel)}
                onChange={(option) => setSelectedLevel(option?.value || "")}
                options={levelOptions}
                placeholder="Select Level"
                isClearable
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </Field> */}
          <Field label="Teacher">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={teacherOptions.find(option => option.value === selectedTeacher)}
                onChange={(option) => setSelectedTeacher(option?.value || "")}
                options={teacherOptions}
                placeholder={"Select Teacher" }
                isClearable
                isSearchable
                onInputChange={(inputValue) => {
                  filterTeachers(inputValue || "");
                }}
                onFocus={() => {
                  if (selectedCentre && selectedCourse && teachers.length === 0) {
                    fetchTeachers();
                  }
                }}
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                // isDisabled={!selectedCentre || !selectedCourse || teacherLoading}
                isLoading={teacherLoading}
              />
            )}
          </Field>
          <Field label="Student">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={studentOptions.find(option => option.value === selectedStudent)}
                onChange={(option) => setSelectedStudent(option?.value || "")}
                options={studentOptions}
                placeholder={"Select Student" }
                isClearable
                isSearchable
                onInputChange={(inputValue) => {
                  filterStudents(inputValue || "");
                }}
                onFocus={() => {
                  if (selectedCentre && selectedCourse && students.length === 0) {
                    fetchStudents();
                  }
                }}
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
                // isDisabled={!selectedCentre || !selectedCourse || loading}
                isLoading={loading}
              />
            )}
          </Field>
          <Field label="Age Group">
            {(ctl) => (
              <Select
                inputId={ctl.id}
                aria-describedby={ctl['aria-describedby']}
                value={ageGroupOptions.find(option => option.value === selectedAgeGroup)}
                onChange={(option) => setSelectedAgeGroup(option?.value || "")}
                options={ageGroupOptions}
                placeholder="select Age"
                isClearable
                className="react-select-container"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            )}
          </Field>
          <div className="ds-toolbar__end">
            <Button variant="primary" onClick={fetchScheduleData} loading={scheduleLoading}>
              {scheduleLoading ? "Loading..." : "Apply"}
            </Button>
          </div>
        </Toolbar>
      </Card>

      {/* Scrollable Calendar Container */}
      <Card flush className="offline-batch__grid">
      <div className="calendar-container">
        {/* Fixed Header Row */}
        <div className="sticky-header">
          <div className="header-row">
        
          </div>
          <div className="days-row">
            <div className="time-spacer"></div>
            {days.map((day) => (
              <div key={day} className="day-header">
                {day}
              </div>
            ))}
          </div>
        </div>

        {/* Scrollable Table Content */}
        <Table className="calendar-table">
        <tbody>
          {scheduleLoading ? (
            <tr>
              <td colSpan={8} className="calendar-state">
                <Loader label="Loading schedule data..." />
              </td>
            </tr>
          ) : Object.keys(scheduleData).length === 0 ? (
            <tr>
              <td colSpan={8} className="calendar-state">
                <EmptyState
                  title="No batches to show"
                  description="Please select City, Centre & Course & click on apply to view the batches."
                />
              </td>
            </tr>
          ) : (
            Object.keys(scheduleData).sort((a, b) => {
              // Extract start time from display format (e.g., "7 AM-8 AM" -> "7 AM")
              const getStartTime = (timeStr: string) => {
                const startTime = timeStr.split('-')[0].trim();
                const hour = parseInt(startTime.split(' ')[0]);
                const period = startTime.split(' ')[1];
                // Convert to 24-hour format for comparison
                if (period === 'AM' && hour === 12) return 0;
                if (period === 'AM') return hour;
                if (period === 'PM' && hour === 12) return 12;
                return hour + 12;
              };
              
              return getStartTime(a) - getStartTime(b);
            }).map((time) => (
              <tr key={time}>
                <td className="time-cell">
                  {time}
                </td>
                          {days.map((day) => {
                  const dayData = scheduleData[time]?.[day] || [];
                  return (
                  <td key={day} className="day-cell">
                      {dayData.length > 0 && dayData.map((batch: Batch, idx: number) => (
                        <div
                          key={batch.id}
                          onClick={() => handleBatchClick(batch)}
                          className={`class-block ${batch.studentCount === 0 ? 'no-students' : ''}`}
                        >
                          <div className="class-header">
                            <div className="teacher-info">
                              <span className="teacher-name">{batch.teacher}</span>
                            </div>
                            <div className="student-count">
                              <span className="count-badge">{batch.studentCount}</span>
                            </div>
                          </div>
                          {/* <div className="class-details">
                            <div className="level-info">
                              <span className="level-value">
                                {Array.isArray(batch.level) ? (
                                  batch.level.length > 1 ? (
                                    <span title={batch.level.join(", ")}>
                                      {batch.level
                                        .filter(level => level && level.trim() !== '')
                                        .map((level, index) => (
                                          <span key={index} className="level-abbreviation">
                                            {level.charAt(0).toUpperCase()}
                                          </span>
                                        ))}
                                    </span>
                                  ) : (
                                    batch.level[0] && batch.level[0].trim() !== '' ? batch.level[0] : '-'
                                  )
                                ) : batch.level}
                              </span>
                            </div>
                          </div> */}
                        </div>
                      ))}
                            </td>
                          );
                        })}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      </div>
      </Card>

      {/* Separate Modal Component */}
      <BatchDetailsModal 
        isOpen={isModalOpen}
        toggle={closeModal}
        selectedBatch={selectedBatch}
        selectedCourse={selectedCourse}
      />
    </Page>
  );
};

export default BatchScheduler;


